import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import jwt_decode from "jwt-decode";
import ReactMarkdown from "react-markdown";
import { Box, Button, Paper, Tab, Tabs, Typography } from "@material-ui/core";
import { CheckCircleOutline, HighlightOff, RadioButtonUnchecked } from "@material-ui/icons";
import qlearnLogo from "../../images/logo_light.png";
import blurImg from "../../images/blurred-exp.png";
import { api } from "../../services/apiService";
import {
  appendDailyCampaignParams,
  buildOpaqueDailyLoginPath,
  buildOpaqueDailyPagePath,
  isDailyAbsiteEnabled,
  isOpaqueDailyGrantId,
  sanitizeOpaqueDailyEntry,
} from "../../utils/daily-question-opaque";
import { toExplanationMarkdown } from "../../utils/explanation-markdown";
import ExplanationMarkdownBoundary from "../../components/explanation-markdown-boundary/explanation-markdown-boundary";
import { gtmTrack } from "../../utils/tracking";
import { detectInAppBrowser, inAppBrowserDisplayName } from "../../utils/in-app-browser";
import WeeklyAbsiteSignup from "../../components/weekly-absite-signup/weekly-absite-signup";
import { useRecordingMayRun } from "../../components/replay-consent/use-replay-consent";
import { isReplayFeatureAvailable } from "../../utils/replay-consent";
import { leaveRecordingPage, replaceAddress } from "../../utils/replay-document";
import "./daily-absite-question.scss";

const googleLogo = require("../../images/google-icon.png").default;

// This page's own address after an opaque-state change. The campaign keys and
// ad click ids it landed with stay (ads attribution; the conversion linker
// reads them from the address); only the opaque state changes.
const dailyAddress = (opaqueState) => appendDailyCampaignParams(
  buildOpaqueDailyPagePath(opaqueState),
  window.location.search
);
const DAILY_EXAM_TYPE = "ABSITE";
const SESSION_QUESTION_ID_STORAGE_KEY = "qlearn_daily_absite_session_question_id";
const ANSWER_RECEIPT_STORAGE_PREFIX = "qlearn_daily_absite_answer_receipt";
const UNLOCK_GRANT_STORAGE_PREFIX = "qlearn_daily_absite_unlock_grant";
const EXPLANATION_UNLOCKED_STORAGE_PREFIX = "qlearn_daily_absite_explanation_unlocked";
const DAILY_ABSITE_PATH = "/daily-absite-question";
const SESSION_RECHECK_MS = 1000;
// Preserve teaching text and basic formatting without remote media or raw HTML.
const DAILY_MARKDOWN_ELEMENTS = ["p", "strong", "em", "ul", "ol", "li", "blockquote", "code", "br"];

const isNonBlankString = (value) => typeof value === "string" && value.trim().length > 0;
const REVISION_HASH_PATTERN = /^[0-9a-f]{64}$/;
const isExactRevisionIdentity = (revisionId, revisionHash) => (
  typeof revisionHash === "string"
  && REVISION_HASH_PATTERN.test(revisionHash)
  && revisionId === `dqr_${revisionHash}`
);
const hasOnlyKeys = (candidate, allowedKeys) => candidate !== null
  && typeof candidate === "object"
  && !Array.isArray(candidate)
  && Object.keys(candidate).every((key) => allowedKeys.has(key));
const TEASER_FIELDS = new Set([
  "examType", "date", "questionId", "slug", "qlinkId", "revisionId", "revisionHash",
  "revisionStatus", "stem", "answers", "signedOutSessionQuestionId", "answerProof",
  "answerProofExpiresAt",
]);
const ANSWER_FIELDS = new Set(["id", "label"]);
const TOMBSTONE_BODY_FIELDS = new Set([
  "examType", "date", "questionId", "slug", "revisionId", "revisionHash",
  "revisionStatus", "noticeCode", "noticeTitle", "noticeMessage",
  "replacementRevisionId", "replacementRevisionHash",
]);
const TOMBSTONE_RESULT_FIELDS = new Set([...TOMBSTONE_BODY_FIELDS, "status", "errMsg"]);
const SESSION_QUESTION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const GRANT_FIELDS = new Set([
  "grantId", "expiresAt", "examType", "date", "revisionId", "revisionHash",
]);
const isAnswerProofExpired = (candidate) => {
  const expiresAtMs = Date.parse(candidate?.answerProofExpiresAt);
  return !Number.isFinite(expiresAtMs) || Date.now() >= expiresAtMs;
};

const createSessionQuestionId = () => {
  try {
    if (typeof window.crypto?.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    if (typeof window.crypto?.getRandomValues === "function") {
      const bytes = new Uint8Array(24);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    }
  } catch (_) {
    // A missing secure random source fails the public issuance request closed.
  }
  return null;
};

const getOrCreateSessionQuestionId = () => {
  try {
    const existing = window.sessionStorage.getItem(SESSION_QUESTION_ID_STORAGE_KEY);
    if (SESSION_QUESTION_ID_PATTERN.test(existing || "")) return existing;
  } catch (_) {
    // The generated value can still live in component state for this page view.
  }

  const created = createSessionQuestionId();
  if (!created) return null;
  try {
    window.sessionStorage.setItem(SESSION_QUESTION_ID_STORAGE_KEY, created);
  } catch (_) {
    // Storage is optional; the HMAC proof remains bound to the in-memory value.
  }
  return created;
};

const isSignedIn = (storage = typeof window !== "undefined" ? window.localStorage : undefined) => {
  try {
    const token = storage?.getItem("token");
    if (!token) return false;
    const decodedToken = jwt_decode(token);
    const expMs = Number(decodedToken?.exp) * 1000;
    if (!Number.isFinite(expMs) || Date.now() >= expMs) return false;
    return storage?.getItem("isProfileComplete") !== "false";
  } catch (_) {
    return false;
  }
};

const isValidTeaser = (candidate, expectedSessionQuestionId) => {
  if (!candidate
      || !hasOnlyKeys(candidate, TEASER_FIELDS)
      || candidate.status
      || candidate.examType !== DAILY_EXAM_TYPE
      || candidate.revisionStatus !== "ACTIVE"
      || !isNonBlankString(candidate.date)
      || !isNonBlankString(candidate.questionId)
      || !isNonBlankString(candidate.slug)
      || !isExactRevisionIdentity(candidate.revisionId, candidate.revisionHash)
      || candidate.signedOutSessionQuestionId !== expectedSessionQuestionId
      || !isNonBlankString(candidate.answerProof)
      || !isNonBlankString(candidate.answerProofExpiresAt)
      || !isNonBlankString(candidate.stem)
      || !Array.isArray(candidate.answers)
      || candidate.answers.length < 2
      || (Object.prototype.hasOwnProperty.call(candidate, "qlinkId")
        && !isNonBlankString(candidate.qlinkId))) {
    return false;
  }

  const answerIds = candidate.answers.map((answer) => answer?.id);
  return candidate.answers.every((answer) => (
    hasOnlyKeys(answer, ANSWER_FIELDS)
    && Object.keys(answer).length === ANSWER_FIELDS.size
    && isNonBlankString(answer?.id)
    && isNonBlankString(answer?.label)
  ))
    && new Set(answerIds).size === answerIds.length;
};

const TERMINAL_NOTICES = Object.freeze({
  WITHDRAWN: Object.freeze({
    noticeCode: "MEDICAL_WITHDRAWAL",
    noticeTitle: "Question unavailable",
    noticeMessage: "This question has been withdrawn and is no longer available.",
  }),
  CORRECTED: Object.freeze({
    noticeCode: "CONTENT_CORRECTION",
    noticeTitle: "Question corrected",
    noticeMessage: "This question has been corrected and the original version is no longer available.",
  }),
});

const isValidTombstone = (candidate) => {
  const approvedNotice = TERMINAL_NOTICES[candidate?.revisionStatus];
  if (!candidate
      || !hasOnlyKeys(candidate, TOMBSTONE_RESULT_FIELDS)
      || candidate.status !== 410
      || candidate.examType !== DAILY_EXAM_TYPE
      || !isNonBlankString(candidate.date)
      || !isNonBlankString(candidate.questionId)
      || !isNonBlankString(candidate.slug)
      || !isExactRevisionIdentity(candidate.revisionId, candidate.revisionHash)
      || !approvedNotice
      || candidate.noticeCode !== approvedNotice.noticeCode
      || candidate.noticeTitle !== approvedNotice.noticeTitle
      || candidate.noticeMessage !== approvedNotice.noticeMessage) {
    return false;
  }

  const replacementIsValid = candidate.revisionStatus === "WITHDRAWN"
    ? !Object.prototype.hasOwnProperty.call(candidate, "replacementRevisionId")
      && !Object.prototype.hasOwnProperty.call(candidate, "replacementRevisionHash")
    : isExactRevisionIdentity(
      candidate.replacementRevisionId,
      candidate.replacementRevisionHash
    );
  if (!replacementIsValid) return false;

  if (!Object.prototype.hasOwnProperty.call(candidate, "errMsg")) return true;
  if (!hasOnlyKeys(candidate.errMsg, TOMBSTONE_BODY_FIELDS)) return false;
  return Object.entries(candidate.errMsg).every(([key, value]) => candidate[key] === value);
};

const answerReceiptStorageKey = (question) => [
  ANSWER_RECEIPT_STORAGE_PREFIX,
  question.examType,
  question.date,
  question.questionId,
  question.revisionId,
  question.revisionHash,
  question.signedOutSessionQuestionId,
].join(":");

const explanationUnlockedStorageKey = (question) => [
  EXPLANATION_UNLOCKED_STORAGE_PREFIX,
  question.examType,
  question.date,
  question.questionId,
  question.revisionId,
  question.revisionHash,
].join(":");

const unlockGrantStorageKey = (question, receipt) => [
  UNLOCK_GRANT_STORAGE_PREFIX,
  question.examType,
  question.date,
  question.questionId,
  question.revisionId,
  question.revisionHash,
  question.signedOutSessionQuestionId,
  receipt.receiptId,
].join(":");

const isMatchingAnswerReceipt = (question, candidate) => {
  if (!question
      || !candidate
      || candidate.status
      || !isNonBlankString(candidate.receiptId)
      || candidate.eventName !== "daily_absite_question_answered"
      || candidate.examType !== question.examType
      || candidate.date !== question.date
      || candidate.questionId !== question.questionId
      || candidate.revisionId !== question.revisionId
      || candidate.revisionHash !== question.revisionHash
      || !isNonBlankString(candidate.answerId)
      || typeof candidate.correct !== "boolean"
      || !isNonBlankString(candidate.correctAnswerId)
      || !isNonBlankString(candidate.briefExplanation)) {
    return false;
  }
  const answerIds = question.answers.map((answer) => answer.id);
  return answerIds.includes(candidate.answerId)
    && answerIds.includes(candidate.correctAnswerId)
    && candidate.correct === (candidate.answerId === candidate.correctAnswerId);
};

const isMatchingUnlockGrant = (question, candidate) => (
  hasOnlyKeys(candidate, GRANT_FIELDS)
  && Object.keys(candidate).length === GRANT_FIELDS.size
  && isOpaqueDailyGrantId(candidate.grantId)
  && Number.isFinite(Date.parse(candidate.expiresAt))
  && Date.parse(candidate.expiresAt) > Date.now()
  && candidate.examType === question?.examType
  && candidate.date === question?.date
  && candidate.revisionId === question?.revisionId
  && candidate.revisionHash === question?.revisionHash
);

const isSameQuestionRevision = (expected, candidate) => (
  candidate?.examType === expected?.examType
  && candidate?.date === expected?.date
  && candidate?.questionId === expected?.questionId
  && candidate?.slug === expected?.slug
  && candidate?.revisionId === expected?.revisionId
  && candidate?.revisionHash === expected?.revisionHash
  && candidate?.stem === expected?.stem
  && JSON.stringify(candidate?.answers) === JSON.stringify(expected?.answers)
);

const questionDiagnostics = (question) => ({
  question_id: question?.questionId,
  question_date: question?.date,
  question_slug: question?.slug,
  question_revision_id: question?.revisionId,
  question_revision_hash: question?.revisionHash,
});

const buildEventPayload = (touchId, extra = {}) => ({
  exam_type: DAILY_EXAM_TYPE,
  ...(touchId ? { opaque_touch_id: touchId } : {}),
  ...extra,
});

function EnabledDailyAbsiteQuestionPage() {
  const [opaqueEntry] = useState(() => sanitizeOpaqueDailyEntry());
  const [sessionQuestionId] = useState(() => getOrCreateSessionQuestionId());
  const [immutableIdentity] = useState(() => (opaqueEntry.date ? {
    date: opaqueEntry.date,
    revisionId: opaqueEntry.revisionId,
    revisionHash: opaqueEntry.revisionHash,
  } : null));
  const [grantId, setGrantId] = useState(opaqueEntry.grantId || null);
  const [entryTouchId] = useState(opaqueEntry.touchId || null);
  const [touchId, setTouchId] = useState(opaqueEntry.touchId || null);
  const [question, setQuestion] = useState(null);
  const [questionLoading, setQuestionLoading] = useState(true);
  const [questionError, setQuestionError] = useState(false);
  const [entryExpired, setEntryExpired] = useState(false);
  const [tombstone, setTombstone] = useState(null);
  const [answerReceipt, setAnswerReceipt] = useState(null);
  const [answerSubmitting, setAnswerSubmitting] = useState(false);
  const [answerError, setAnswerError] = useState(false);
  const [fullExplanation, setFullExplanation] = useState(null);
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [explanationError, setExplanationError] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantError, setGrantError] = useState(false);
  const [grantRetryNonce, setGrantRetryNonce] = useState(0);
  const [signedIn, setSignedIn] = useState(() => isSignedIn());
  const [unlockMethod, setUnlockMethod] = useState("google");
  const [unlockMethodPicked, setUnlockMethodPicked] = useState(false);
  const [inAppBrowser] = useState(() => detectInAppBrowser());
  // Wherever recording may run (M3: the feature is built in, recording is not
  // turned off, no GPC, and this page has never held an address off the
  // journey), the grant never reaches the address or the markup: the address
  // writes below go through replaceAddress(), which skips hazardous ones (the
  // grant also lives in sessionStorage), and the unlock button carries no link
  // target, because the target holds the grant. It leaves by a full page load.
  const recording = useRecordingMayRun();
  const explanationRef = useRef(null);
  const explanationRequestKeyRef = useRef(null);
  const explanationAttemptsRef = useRef({ requestKey: null, attempts: 0 });
  const grantRecoveryAttemptedRef = useRef(null);
  const grantIssuanceRef = useRef({ receiptId: null, attempts: 0 });
  const explanationUnlockTrackedRef = useRef(null);
  const answerSubmissionInFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const selectedAnswer = answerReceipt
    ? question?.answers?.find((answer) => answer.id === answerReceipt.answerId)
    : null;

  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    let active = true;

    if (!sessionQuestionId) {
      setQuestionLoading(false);
      setQuestionError(true);
      return () => { active = false; };
    }

    const teaserRequest = immutableIdentity
      ? api.getImmutableDailyQuestion(
        DAILY_EXAM_TYPE,
        immutableIdentity.date,
        immutableIdentity.revisionId,
        immutableIdentity.revisionHash,
        sessionQuestionId
      )
      : (entryTouchId
        ? api.getDailyQuestionByTouch(entryTouchId, sessionQuestionId)
        : api.getDailyQuestion(DAILY_EXAM_TYPE, sessionQuestionId));
    teaserRequest.then((response) => {
      if (!active) return;
      setQuestionLoading(false);
      const matchesImmutableIdentity = !immutableIdentity || (
        response?.examType === DAILY_EXAM_TYPE
        && response?.date === immutableIdentity.date
        && response?.revisionId === immutableIdentity.revisionId
        && response?.revisionHash === immutableIdentity.revisionHash
      );
      if (Number(response?.status) === 404 && (immutableIdentity || entryTouchId)) {
        setQuestion(null);
        setQuestionError(true);
        setEntryExpired(true);
        return;
      }
      if (isValidTombstone(response) && matchesImmutableIdentity) {
        setQuestion(null);
        setQuestionError(false);
        setEntryExpired(false);
        setTombstone(response);
        setAnswerReceipt(null);
        setGrantId(null);
        setFullExplanation(null);
        try {
          replaceAddress(
            window.history.state,
            dailyAddress(immutableIdentity || {})
          );
        } catch (_) {
          // URL cleanup is best-effort; terminal server state still blocks content.
        }
        try {
          const terminalIdentity = {
            ...response,
            signedOutSessionQuestionId: sessionQuestionId,
          };
          const receiptKey = answerReceiptStorageKey(terminalIdentity);
          const storedReceipt = JSON.parse(window.sessionStorage.getItem(receiptKey));
          if (isNonBlankString(storedReceipt?.receiptId)) {
            window.sessionStorage.removeItem(
              unlockGrantStorageKey(terminalIdentity, storedReceipt)
            );
          }
          window.sessionStorage.removeItem(receiptKey);
          window.localStorage.removeItem(explanationUnlockedStorageKey(terminalIdentity));
        } catch (_) {
          // Storage denial cannot restore withdrawn medical content.
        }
        return;
      }
      if (!isValidTeaser(response, sessionQuestionId) || !matchesImmutableIdentity) {
        setQuestionError(true);
        gtmTrack("daily_absite_question_unavailable", buildEventPayload(entryTouchId, {
          response_status: response?.status || "invalid_payload",
        }));
        return;
      }

      setEntryExpired(false);
      setQuestion(response);
      try {
        const storedReceipt = JSON.parse(
          window.sessionStorage.getItem(answerReceiptStorageKey(response))
        );
        if (isMatchingAnswerReceipt(response, storedReceipt)) {
          if (!opaqueEntry.grantId) {
            const storedGrantId = window.sessionStorage.getItem(
              unlockGrantStorageKey(response, storedReceipt)
            );
            if (isOpaqueDailyGrantId(storedGrantId)) {
              setGrantId((currentGrantId) => currentGrantId || storedGrantId);
            } else if (storedGrantId !== null) {
              window.sessionStorage.removeItem(
                unlockGrantStorageKey(response, storedReceipt)
              );
            }
          }
          // Restore authority-bearing state before the receipt enables the
          // issuance effect; React 17 does not batch promise callbacks.
          setAnswerReceipt(storedReceipt);
        }
      } catch (_) {
        // A malformed or unavailable browser store never becomes answer truth.
      }
      gtmTrack("daily_absite_question_viewed", buildEventPayload(entryTouchId, {
        ...questionDiagnostics(response),
      }));
    }, () => {
      if (!active) return;
      setQuestionLoading(false);
      setQuestionError(true);
    });

    return () => { active = false; };
  }, [entryTouchId, immutableIdentity, opaqueEntry.grantId, sessionQuestionId]);

  useEffect(() => {
    if (!question || !answerReceipt || !isOpaqueDailyGrantId(grantId)) return;
    try {
      window.sessionStorage.setItem(
        unlockGrantStorageKey(question, answerReceipt),
        grantId
      );
    } catch (_) {
      // The in-memory bearer can still complete the current server-authorized flow.
    }
  }, [answerReceipt, grantId, question]);

  useEffect(() => {
    if (!question || !answerReceipt || grantId) return undefined;

    if (grantIssuanceRef.current.receiptId !== answerReceipt.receiptId) {
      grantIssuanceRef.current = { receiptId: answerReceipt.receiptId, attempts: 0 };
    }
    if (grantIssuanceRef.current.attempts >= 3) return undefined;

    grantIssuanceRef.current.attempts += 1;
    const attemptNumber = grantIssuanceRef.current.attempts;
    const attemptTouchId = attemptNumber < 3 ? touchId : null;
    if (attemptNumber === 3 && touchId) {
      setTouchId(null);
      try {
        replaceAddress(
          window.history.state,
          dailyAddress(immutableIdentity || {})
        );
      } catch (_) {
        // The final attempt request is still honestly unattributed in memory.
      }
    }

    let active = true;
    setGrantLoading(true);
    setGrantError(false);
    api.issueDailyQuestionUnlockGrant(question.examType, {
      receiptId: answerReceipt.receiptId,
      date: question.date,
      revisionId: question.revisionId,
      revisionHash: question.revisionHash,
      signedOutSessionQuestionId: question.signedOutSessionQuestionId,
      ...(attemptTouchId ? { touchId: attemptTouchId } : {}),
    }).then((response) => {
      if (!active) return;
      setGrantLoading(false);
      if (!isMatchingUnlockGrant(question, response)) {
        setGrantError(true);
        return;
      }
      setGrantId(response.grantId);
      try {
        replaceAddress(
          window.history.state,
          dailyAddress({
            ...(immutableIdentity || {}),
            touchId: attemptTouchId,
            grantId: response.grantId,
          })
        );
      } catch (_) {
        // In-memory state still carries only the opaque identifiers.
      }
    }, () => {
      if (!active) return;
      setGrantLoading(false);
      setGrantError(true);
    });

    return () => { active = false; };
    // touchId deliberately is not a dependency: the third attempt removes it
    // from state without authoring an accidental fourth issuance request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerReceipt, grantId, grantRetryNonce, immutableIdentity, question]);

  useEffect(() => {
    const refreshSessionState = () => {
      const activeSession = isSignedIn();
      setSignedIn(activeSession);
      if (!activeSession) {
        setFullExplanation(null);
        setExplanationLoading(false);
        setExplanationError(false);
        explanationRequestKeyRef.current = null;
      }
    };

    window.addEventListener("storage", refreshSessionState);
    window.addEventListener("focus", refreshSessionState);
    window.addEventListener("pageshow", refreshSessionState);
    const recheckInterval = window.setInterval(refreshSessionState, SESSION_RECHECK_MS);
    return () => {
      window.removeEventListener("storage", refreshSessionState);
      window.removeEventListener("focus", refreshSessionState);
      window.removeEventListener("pageshow", refreshSessionState);
      window.clearInterval(recheckInterval);
    };
  }, []);

  useEffect(() => {
    if (!signedIn || !selectedAnswer || !question || !grantId) return undefined;

    // Auth may have expired or been cleared in another tab after this public
    // page mounted. Recheck immediately before the protected request so the
    // page relocks instead of sending stale or null credentials.
    if (!isSignedIn()) {
      setSignedIn(false);
      setFullExplanation(null);
      return undefined;
    }

    const requestKey = [question.examType, question.date, question.questionId,
      question.revisionId, question.revisionHash, grantId].join(":");
    if (explanationAttemptsRef.current.requestKey !== requestKey) {
      explanationAttemptsRef.current = { requestKey, attempts: 0 };
    }
    if (explanationAttemptsRef.current.attempts >= 3) return undefined;
    if (explanationRequestKeyRef.current === requestKey) return undefined;
    explanationRequestKeyRef.current = requestKey;
    explanationAttemptsRef.current.attempts += 1;

    let active = true;
    setExplanationLoading(true);
    setExplanationError(false);
    setFullExplanation(null);
    api.getDailyQuestionExplanation(
      question.examType,
      question.date,
      question.revisionId,
      question.revisionHash,
      grantId
    ).then((response) => {
      if (!active) return;
      setExplanationLoading(false);
      // The session can disappear while the protected request is in flight.
      // Recheck at the state-commit boundary so a late response can never
      // render protected content after same-tab logout or token expiry.
      if (!isSignedIn()) {
        setSignedIn(false);
        setFullExplanation(null);
        setExplanationError(false);
        explanationRequestKeyRef.current = null;
        return;
      }
      if (response?.status
          || response?.examType !== question.examType
          || response?.date !== question.date
          || response?.questionId !== question.questionId
          || response?.slug !== question.slug
          || response?.revisionId !== question.revisionId
          || response?.revisionHash !== question.revisionHash
          || !isNonBlankString(response?.fullExplanation)) {
        const recoveryKey = unlockGrantStorageKey(question, answerReceipt);
        if (response?.status === 403) {
          try {
            window.sessionStorage.removeItem(recoveryKey);
          } catch (_) {
            // Clearing in-memory state is sufficient to prevent reuse this visit.
          }
          try {
            replaceAddress(
              window.history.state,
              dailyAddress({ ...(immutableIdentity || {}), touchId })
            );
          } catch (_) {
            // The rejected bearer is still removed from component/storage state.
          }
          if (grantRecoveryAttemptedRef.current !== recoveryKey) {
            grantRecoveryAttemptedRef.current = recoveryKey;
            explanationRequestKeyRef.current = null;
            setGrantId(null);
            setExplanationError(false);
            return;
          }
        }
        setExplanationError(true);
        gtmTrack("daily_absite_explanation_unavailable", buildEventPayload(touchId, {
          ...questionDiagnostics(question),
          response_status: response?.status || "invalid_payload",
        }));
        return;
      }
      setFullExplanation(response.fullExplanation);
      const unlockKey = explanationUnlockedStorageKey(question);
      let alreadyTracked = explanationUnlockTrackedRef.current === unlockKey;
      try {
        alreadyTracked = alreadyTracked || window.localStorage.getItem(unlockKey) === "1";
      } catch (_) {
        // In-memory dedupe still protects the current page in privacy mode.
      }
      if (!alreadyTracked) {
        gtmTrack("daily_absite_explanation_unlocked", buildEventPayload(touchId, {
          ...questionDiagnostics(question),
        }));
        explanationUnlockTrackedRef.current = unlockKey;
        try {
          window.localStorage.setItem(unlockKey, "1");
        } catch (_) {
          // Analytics dedupe must not block the rationale itself.
        }
      }
    }, () => {
      if (!active) return;
      setExplanationLoading(false);
      setFullExplanation(null);
      setExplanationError(true);
    });

    return () => { active = false; };
  }, [answerReceipt, grantId, grantRetryNonce, immutableIdentity, question,
    selectedAnswer, signedIn, touchId]);

  const googleUnlockPath = useMemo(() => buildOpaqueDailyLoginPath({
    auth: "google", ...(immutableIdentity || {}), touchId, grantId,
  }), [grantId, immutableIdentity, touchId]);
  const phoneUnlockPath = useMemo(() => buildOpaqueDailyLoginPath({
    auth: "phone", ...(immutableIdentity || {}), touchId, grantId,
  }), [grantId, immutableIdentity, touchId]);

  const completeAnswerFailure = (candidateQuestion, response) => {
    if (!mountedRef.current) return;
    answerSubmissionInFlightRef.current = false;
    setAnswerSubmitting(false);
    setAnswerError(true);
    gtmTrack("daily_absite_answer_receipt_unavailable", buildEventPayload(touchId, {
      ...questionDiagnostics(candidateQuestion),
      response_status: response?.status || response?.errStatus || "invalid_payload",
    }));
  };

  const refreshAnswerProof = (candidateQuestion) => (
    (immutableIdentity
      ? api.getImmutableDailyQuestion(
        DAILY_EXAM_TYPE,
        immutableIdentity.date,
        immutableIdentity.revisionId,
        immutableIdentity.revisionHash,
        sessionQuestionId
      )
      : (entryTouchId
        ? api.getDailyQuestionByTouch(entryTouchId, sessionQuestionId)
        : api.getDailyQuestion(DAILY_EXAM_TYPE, sessionQuestionId))
    ).then((refreshedQuestion) => {
      if (!mountedRef.current
          || !isValidTeaser(refreshedQuestion, sessionQuestionId)
          || !isSameQuestionRevision(candidateQuestion, refreshedQuestion)
          || isAnswerProofExpired(refreshedQuestion)) {
        return null;
      }
      setQuestion(refreshedQuestion);
      return refreshedQuestion;
    })
  );

  const submitAnswer = (answer, candidateQuestion, allowProofRefresh) => {
    api.submitDailyQuestionAnswer(candidateQuestion.examType, {
      date: candidateQuestion.date,
      revisionId: candidateQuestion.revisionId,
      revisionHash: candidateQuestion.revisionHash,
      signedOutSessionQuestionId: candidateQuestion.signedOutSessionQuestionId,
      answerProof: candidateQuestion.answerProof,
      answerId: answer.id,
    }).then((response) => {
      if (!mountedRef.current) return;
      const responseStatus = Number(response?.status || response?.errStatus);
      if (allowProofRefresh && responseStatus === 400) {
        refreshAnswerProof(candidateQuestion).then((refreshedQuestion) => {
          if (!refreshedQuestion) {
            completeAnswerFailure(candidateQuestion, response);
            return;
          }
          submitAnswer(answer, refreshedQuestion, false);
        }, () => completeAnswerFailure(candidateQuestion, {status: "network_error"}));
        return;
      }

      answerSubmissionInFlightRef.current = false;
      setAnswerSubmitting(false);
      if (!isMatchingAnswerReceipt(candidateQuestion, response)) {
        completeAnswerFailure(candidateQuestion, response);
        return;
      }

      setAnswerReceipt(response);
      try {
        window.sessionStorage.setItem(
          answerReceiptStorageKey(candidateQuestion),
          JSON.stringify(response)
        );
      } catch (_) {
        // The server receipt remains authoritative in component state.
      }
      gtmTrack("daily_absite_answer_receipt_observed", buildEventPayload(touchId, {
        ...questionDiagnostics(candidateQuestion),
        answer_state: response.correct ? "correct" : "incorrect",
      }));
      window.requestAnimationFrame?.(() => {
        if (typeof explanationRef.current?.scrollIntoView === "function") {
          explanationRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    }, () => completeAnswerFailure(candidateQuestion, {status: "network_error"}));
  };

  const handleAnswerClick = (answer) => {
    if (answerReceipt || answerSubmissionInFlightRef.current) return;
    answerSubmissionInFlightRef.current = true;
    setAnswerSubmitting(true);
    setAnswerError(false);

    if (isAnswerProofExpired(question)) {
      refreshAnswerProof(question).then((refreshedQuestion) => {
        if (!refreshedQuestion) {
          completeAnswerFailure(question, { status: "expired_proof" });
          return;
        }
        submitAnswer(answer, refreshedQuestion, false);
      }, () => completeAnswerFailure(question, {status: "network_error"}));
      return;
    }
    submitAnswer(answer, question, true);
  };

  // Phone by default inside a social app's browser, decided at render time so
  // the Google tab never flashes first; a tab the visitor picks always wins.
  const effectiveUnlockMethod = inAppBrowser.inApp && !unlockMethodPicked ? "phone" : unlockMethod;
  const inAppBrowserName = inAppBrowserDisplayName(inAppBrowser.app);
  const showInAppGoogleNote = inAppBrowser.inApp && effectiveUnlockMethod === "google";
  const unlockPath = effectiveUnlockMethod === "phone" ? phoneUnlockPath : googleUnlockPath;
  const handleUnlockMethodChange = (_, nextMethod) => {
    setUnlockMethod(nextMethod);
    setUnlockMethodPicked(true);
  };
  const handleSignupIntent = () => {
    if (!signedIn) {
      gtmTrack("daily_absite_signup_intent", buildEventPayload(touchId, {
        ...questionDiagnostics(question),
        cta_target: "/login",
        auth_method: effectiveUnlockMethod,
        in_app_browser: inAppBrowser.inApp ? inAppBrowser.app : "none",
      }));
    }
  };

  const canRetryGrant = Boolean(answerReceipt)
    && grantIssuanceRef.current.attempts < 3;
  const canRetryExplanation = explanationError
    && isOpaqueDailyGrantId(grantId)
    && explanationAttemptsRef.current.attempts < 3;
  const canRetryUnlock = explanationError ? canRetryExplanation : canRetryGrant;
  const handleGrantRetry = () => {
    if (!question || !answerReceipt || !canRetryUnlock) return;
    if (canRetryExplanation && !grantError) {
      explanationRequestKeyRef.current = null;
      setExplanationError(false);
      setFullExplanation(null);
      setGrantRetryNonce((current) => current + 1);
      return;
    }
    try {
      window.sessionStorage.removeItem(unlockGrantStorageKey(question, answerReceipt));
    } catch (_) {
      // In-memory authority is cleared below even when storage is unavailable.
    }
    explanationRequestKeyRef.current = null;
    setGrantId(null);
    setFullExplanation(null);
    setGrantError(false);
    setExplanationError(false);
    try {
      replaceAddress(
        window.history.state,
        dailyAddress({ ...(immutableIdentity || {}), touchId })
      );
    } catch (_) {
      // The retry still uses the sanitized in-memory identity.
    }
    setGrantRetryNonce((current) => current + 1);
  };

  const correctedQuestionPath = tombstone?.revisionStatus === "CORRECTED"
    ? buildOpaqueDailyPagePath({
      date: tombstone.date,
      revisionId: tombstone.replacementRevisionId,
      revisionHash: tombstone.replacementRevisionHash,
    })
    : null;

  const handlePlansClick = () => {
    gtmTrack("daily_absite_plans_cta_clicked", buildEventPayload(touchId, {
      ...questionDiagnostics(question),
      cta_target: "/subscriptions",
    }));
  };

  const handlePracticeClick = () => {
    gtmTrack("daily_absite_practice_cta_clicked", buildEventPayload(touchId, {
      ...questionDiagnostics(question),
      cta_target: "/create-quiz",
      explanation_state: fullExplanation ? "unlocked" : "locked",
    }));
  };

  return (
    <Box className="daily-absite-page">
      <Box className="daily-absite-shell">
        <Box className="daily-absite-header">
          <Box className="daily-absite-identity">
            <Box className="daily-absite-logo-mark">
              <img alt="QLearn" className="daily-absite-logo" src={qlearnLogo} />
            </Box>
            <Box className="daily-absite-identity-copy">
              <Typography className="daily-absite-identity-title" variant="subtitle2">
                Medlearnity QLearn
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className="daily-absite-layout">
          <Box className="daily-absite-copy">
            <Typography component="h1" variant="h3">Daily ABSITE Case Review</Typography>
            <Typography className="daily-absite-subtitle" variant="h6">
              One free ABSITE case. Answer, unlock the rationale, then keep practicing.
            </Typography>
          </Box>

          <Paper className="daily-absite-question" elevation={2}>
            <Box className="daily-absite-question-inner">
              <Box className="daily-absite-question-meta">
                <Typography className="daily-absite-kicker" variant="overline">
                  Today&apos;s ABSITE Question
                </Typography>
              </Box>
              {questionLoading && <Typography role="status">Loading today&apos;s question…</Typography>}
              {tombstone && (
                <Box className="daily-absite-terminal-state">
                  <Typography role="alert">
                    <strong>{tombstone.noticeTitle}.</strong> {tombstone.noticeMessage}
                  </Typography>
                  <Box className="daily-absite-terminal-actions">
                    {correctedQuestionPath && (
                      <Link to={correctedQuestionPath}>See corrected question</Link>
                    )}
                    <Link to={DAILY_ABSITE_PATH}>See today&apos;s question</Link>
                  </Box>
                </Box>
              )}
              {questionError && (
                <Box className="daily-absite-terminal-state">
                  <Typography role="alert">
                    Today&apos;s daily question is unavailable. Please try again shortly.
                  </Typography>
                  {entryExpired && (
                    <Link to={DAILY_ABSITE_PATH}>See today&apos;s question</Link>
                  )}
                </Box>
              )}

              {question && (
                <>
                  <Typography component="h2" variant="h5">{question.stem}</Typography>
                  <Box className="daily-absite-answers">
                    {question.answers.map((answer) => {
                      const isCorrect = answer.id === answerReceipt?.correctAnswerId;
                      const isSelected = selectedAnswer?.id === answer.id;
                      const revealAsCorrect = Boolean(selectedAnswer) && isCorrect;
                      let stateClassName = "";
                      if (isSelected) stateClassName = `selected ${isCorrect ? "correct" : "incorrect"}`;
                      else if (revealAsCorrect) stateClassName = "revealed correct";
                      const answerIcon = isSelected
                        ? (isCorrect ? <CheckCircleOutline /> : <HighlightOff />)
                        : (revealAsCorrect ? <CheckCircleOutline /> : <RadioButtonUnchecked />);

                      return (
                        <Button
                          className={`daily-absite-answer ${stateClassName}`}
                          disableRipple
                          disabled={Boolean(answerReceipt) || answerSubmitting}
                          key={answer.id}
                          onClick={() => handleAnswerClick(answer)}
                          startIcon={answerIcon}
                          variant="outlined"
                        >
                          {answer.label}
                          {revealAsCorrect && !isSelected && (
                            <span className="daily-absite-sr-only"> (correct answer)</span>
                          )}
                          {isSelected && <span className="daily-absite-sr-only"> (your answer)</span>}
                        </Button>
                      );
                    })}
                  </Box>

                  {answerSubmitting && (
                    <Typography role="status">Checking your answer…</Typography>
                  )}
                  {answerError && (
                    <Typography role="alert">
                      Your answer could not be checked. Please try again.
                    </Typography>
                  )}

                  {selectedAnswer && (
                    <Box className="daily-absite-explanation" ref={explanationRef}>
                      <Box className="daily-absite-explanation-result" role="status">
                        <Typography variant="subtitle2">
                          {answerReceipt.correct ? "Correct" : "Not quite"}
                        </Typography>
                      </Box>
                      <Box className="daily-absite-brief-explanation" data-testid="daily-absite-brief-explanation">
                        <Typography className="daily-absite-markdown" component="div" variant="body2">
                          <ExplanationMarkdownBoundary text={answerReceipt.briefExplanation}>
                            <ReactMarkdown allowedElements={DAILY_MARKDOWN_ELEMENTS} skipHtml unwrapDisallowed>
                              {toExplanationMarkdown(answerReceipt.briefExplanation)}
                            </ReactMarkdown>
                          </ExplanationMarkdownBoundary>
                        </Typography>
                      </Box>
                      {signedIn ? (
                        <Box className="daily-absite-full-explanation">
                          {(grantLoading || explanationLoading) && (
                            <Typography variant="body2">Loading full rationale…</Typography>
                          )}
                          {(grantError || explanationError) && (
                            <Box className="daily-absite-retry-state">
                              <Typography role="alert" variant="body2">
                                The full rationale is unavailable for this answered question.
                              </Typography>
                              {canRetryUnlock && (
                                <Button onClick={handleGrantRetry} variant="text">Try again</Button>
                              )}
                            </Box>
                          )}
                          {fullExplanation && (
                            <Typography className="daily-absite-markdown" component="div" variant="body2">
                              <ExplanationMarkdownBoundary text={fullExplanation}>
                                <ReactMarkdown allowedElements={DAILY_MARKDOWN_ELEMENTS} skipHtml unwrapDisallowed>
                                  {toExplanationMarkdown(fullExplanation)}
                                </ReactMarkdown>
                              </ExplanationMarkdownBoundary>
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box
                          className={`daily-absite-locked-explanation${showInAppGoogleNote ? " daily-absite-locked-explanation--with-note" : ""}`}
                          data-testid="daily-absite-locked-explanation"
                        >
                          <Box aria-hidden="true" className="daily-absite-blurred-preview" data-testid="daily-absite-blurred-preview">
                            <img alt="" className="daily-absite-blurred-preview-image" src={blurImg} />
                          </Box>
                          <Box className="daily-absite-qlink-login-box">
                            <Typography className="daily-absite-unlock-title" variant="subtitle1">
                              Unlock the full rationale
                            </Typography>
                            <Typography className="daily-absite-unlock-copy" variant="body2">
                              Sign in free to unlock the teaching point and keep practicing.
                            </Typography>
                            {grantLoading && (
                              <Typography role="status" variant="body2">Preparing secure unlock…</Typography>
                            )}
                            {grantError && (
                              <Box className="daily-absite-retry-state">
                                <Typography role="alert" variant="body2">
                                  Secure unlock is unavailable for this answered question.
                                </Typography>
                                {canRetryGrant && (
                                  <Button onClick={handleGrantRetry} variant="text">Try again</Button>
                                )}
                              </Box>
                            )}
                            <Tabs
                              className="daily-absite-unlock-tabs"
                              centered
                              indicatorColor="primary"
                              onChange={handleUnlockMethodChange}
                              textColor="primary"
                              value={effectiveUnlockMethod}
                            >
                              <Tab label="Google Sign In" value="google" />
                              <Tab label="Phone Sign In" value="phone" />
                            </Tabs>
                            {showInAppGoogleNote && (
                              <Typography
                                className="daily-absite-inapp-google-note"
                                data-testid="daily-absite-inapp-google-note"
                                id="daily-absite-inapp-google-note"
                                variant="body2"
                              >
                                Google sign-in usually fails inside {inAppBrowserName}. Use Phone sign-in instead.
                              </Typography>
                            )}
                            <Button
                              aria-describedby={showInAppGoogleNote ? "daily-absite-inapp-google-note" : undefined}
                              aria-label={effectiveUnlockMethod === "google" ? "Sign In with Google to unlock explanation" : "Sign In with Phone to unlock explanation"}
                              className={effectiveUnlockMethod === "google" ? "daily-absite-google-unlock" : "daily-absite-phone-unlock"}
                              color={effectiveUnlockMethod === "google" ? "secondary" : "primary"}
                              {...(recording ? {
                                onClick: () => {
                                  handleSignupIntent();
                                  leaveRecordingPage(unlockPath);
                                },
                              } : {
                                component: Link,
                                onClick: handleSignupIntent,
                                to: unlockPath,
                              })}
                              variant={effectiveUnlockMethod === "google" ? "outlined" : "contained"}
                            >
                              {effectiveUnlockMethod === "google" && (
                                <img alt="" aria-hidden="true" className="daily-absite-google-icon" src={googleLogo} />
                              )}
                              {effectiveUnlockMethod === "google" ? "Sign In with Google" : "Sign In with Phone"}
                            </Button>
                            {/* New tabs (review of #896), like the line under the
                                card, so the answered question is not lost. */}
                            <Typography className="daily-absite-legal-copy" variant="caption">
                              By signing in, you agree to QLearn&apos;s{" "}
                              <a href="/terms" rel="noopener noreferrer" target="_blank">
                                Terms<span className="daily-absite-visually-hidden"> (opens in a new tab)</span>
                              </a>
                              {" "}and{" "}
                              <a href="/privacy-policy" rel="noopener noreferrer" target="_blank">
                                Privacy Policy<span className="daily-absite-visually-hidden"> (opens in a new tab)</span>
                              </a>
                              {/* Prior notice of session recording in the agreement
                                  itself (review of #896): text and a policy link, no
                                  control. Only where recording is built in. */}
                              {isReplayFeatureAvailable() && (
                                <>, including{" "}
                                  <a href="/privacy-policy#session-recording" rel="noopener noreferrer" target="_blank">
                                    session recording<span className="daily-absite-visually-hidden"> (opens in a new tab)</span>
                                  </a>
                                </>
                              )}.
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {selectedAnswer && !signedIn && <WeeklyAbsiteSignup />}

                  {signedIn && (fullExplanation ? (
                    <Box className="daily-absite-post-value-actions">
                      <Button
                        className="daily-absite-primary-cta"
                        color="primary"
                        component={Link}
                        onClick={handlePracticeClick}
                        to={{ pathname: "/create-quiz", state: { practiceExam: "ABSITE" } }}
                        variant="contained"
                      >
                        Practice more ABSITE questions
                      </Button>
                      <Button
                        className="daily-absite-secondary-cta"
                        component={Link}
                        onClick={handlePlansClick}
                        to="/subscriptions"
                        variant="outlined"
                      >
                        See QLearn Pro plans
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      className="daily-absite-primary-cta"
                      color="primary"
                      component={Link}
                      onClick={handlePracticeClick}
                      to={{ pathname: "/create-quiz", state: { practiceExam: "ABSITE" } }}
                      variant="contained"
                    >
                      Practice more ABSITE questions
                    </Button>
                  ))}

                  {selectedAnswer && signedIn && (
                    <Box className="daily-absite-weekly-after-actions">
                      <WeeklyAbsiteSignup />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Paper>
          {/* Ordinary page chrome, always visible from the first paint (review
              of #896): the page records from load for cold ad and email
              traffic, so the policy is one tap away. No mention of recording,
              no control. New tabs keep the question as it is. */}
          <Typography className="daily-absite-page-legal" component="p" data-testid="daily-absite-page-legal" variant="caption">
            <a href="/terms" rel="noopener noreferrer" target="_blank">
              Terms<span className="daily-absite-visually-hidden"> (opens in a new tab)</span>
            </a>
            <span aria-hidden="true" className="daily-absite-page-legal-separator"> · </span>
            <a href="/privacy-policy" rel="noopener noreferrer" target="_blank">
              Privacy Policy<span className="daily-absite-visually-hidden"> (opens in a new tab)</span>
            </a>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const DailyAbsiteHoldingPage = () => (
  <Box className="daily-absite-page daily-absite-holding-page">
    <Paper className="daily-absite-holding-card" elevation={2}>
      <img alt="QLearn" className="daily-absite-logo" src={qlearnLogo} />
      <Typography component="h1" variant="h4">Medlearnity QLearn</Typography>
      <Typography variant="body1">Daily ABSITE practice is not available yet.</Typography>
      <Button color="primary" component={Link} to="/login" variant="contained">
        Sign in to QLearn
      </Button>
    </Paper>
  </Box>
);

export default function DailyAbsiteQuestionPage() {
  return isDailyAbsiteEnabled()
    ? <EnabledDailyAbsiteQuestionPage />
    : <DailyAbsiteHoldingPage />;
}
